import { Action } from "@reduxjs/toolkit";
import { TokenStats } from "../../components/Token/TokenForm";
import { Rootstate } from "../../store";
import { ObjectId } from "bson";


export type MapShape = {
    shape: 'circle' | 'square',
    xPos: number
    yPos: number
    color: string
    width: number
    height: number,
    rotation: number,
    dm: boolean,
    _id: ObjectId,
}

export type MapImage = {
    image: string,
    xPos: number,
    yPos: number,
    width: number,
    height: number,
    xScale: number,
    yScale: number,
    rotation: number,
    visible?: boolean,
    dm?: boolean,
    _id: ObjectId,
}

export type InitialMapImage = Omit<MapImage, '_id'> & { _id?: ObjectId };

export type TurnOrder = {
    name: string,
    identifier: string,
    roll: number,
    private?: boolean,
};

export type MapImageWithImageDetails = Omit<MapImage, 'image'> & {
    image: {
        _id: string,
        width: number,
        height: number,
    }
}

export interface Map {
    background: string,
    tokenLayer: Token[],
    spellLayer: MapShape[],
    objectLayer: MapImage[],
    backgroundWidth: number,
    backgroundHeight: number,
    fog: boolean[][],
    turnOrder: TurnOrder[],
    activeTurn?: string
}


export type Token = TokenStats & MapImage;

const defaultState: {
    imageLoading: boolean
    mapId?: string,

    map?: Partial<Map>,
    fogShape?: {
        type: string,
        size: number,
        add: boolean
    },
    mainLayer: 'dm' | 'public'
    mode: 'default' | 'measure' | 'pan',
    selectedElement?: { _id: ObjectId, type: string },
    pings: { x: number, y: number, _id: ObjectId }[],
} = {
    imageLoading: false,
    mapId: undefined,
    mainLayer: 'public',
    mode: 'default',
    pings: [],
};

interface MapReducerAction extends Action {
  value?: string | Token[] | boolean | MapShape[] | TurnOrder[] | ObjectId
  | { x: number, y: number, _id: ObjectId } | { _id: ObjectId, type: string } | boolean[][] | Map | undefined,
}


const gridReducer = (state = defaultState, action: MapReducerAction): typeof defaultState => {
    switch (action.type) {
    case 'MAP/SET_PARTIAL': {
        return {
            ...state,
            map: {
                ...state.map,
                ...action.value as Map,
            },
        };
    }
    case 'MAP/SET_BACKGROUND':
        return {
            ...state,
            imageLoading: true,
            map: {
                ...state.map,
                background: action.value as string,
            },
        };
    case 'MAP/SET_LOADING':
        return {
            ...state,
            imageLoading: action.value as boolean,
        };
    case 'MAP/SET_MAPID':
        return {
            ...state,
            mapId: action.value as string,
        };
    case 'MAP/RESET':
        return {
            mode: 'default',
            mainLayer: 'public',
            imageLoading: false,
            pings: [],
        };
    case 'MAP/SET_TOKENS':
        return {
            ...state,
            map: {
                ...state.map,
                tokenLayer: action.value as Token[],
            },
        };
    case 'MAP/SET_FOG':
        return {
            ...state,
            map: {
                ...state.map,
                fog: action.value as boolean[][],
            },
        };
    case 'MAP/SET_FOG_SHAPE':
        return {
            ...state,
            fogShape: action.value as { type: string, size: number, add: boolean } | undefined,
        };
    case 'MAP/SET_IMAGES':
        return {
            ...state,
            map: {
                ...state.map,
                objectLayer: action.value as MapImage[],
            },
        };
    case 'MAP/SET_SPELLS':
        return {
            ...state,
            map: {
                ...state.map,
                spellLayer: action.value as MapShape[],
            },
        };
    case 'MAP/SET_TURN_ORDER':
        return {
            ...state,
            map: {
                ...state.map,
                turnOrder: action.value as TurnOrder[],
            },
        };
    case 'MAP/SET_ACTIVE_TURN':
        return {
            ...state,
            map: {
                ...state.map,
                activeTurn: action.value as string | undefined,
            },
        };
    case 'MAP/TOGGLE_LAYER':
        return {
            ...state,
            mainLayer: state.mainLayer === 'dm' ? 'public' : 'dm',
        };
    case 'MAP/SET_MODE':
        return {
            ...state,
            mode: action.value as (typeof defaultState)['mode'],
        };
    case 'MAP/SET_SELECTED_ELEMENT':
        return {
            ...state,
            selectedElement: action.value as { _id: ObjectId, type: string } | undefined,
        };
    case 'MAP/ADD_PING':
        return {
            ...state,
            pings: [...state.pings, action.value as { x: number, y: number, _id: ObjectId }],
        };
    case 'MAP/REMOVE_PING':
        return {
            ...state,
            pings: state.pings.filter((ping) => ping._id !== (action.value)),
        };
    default:
        return state;
    }
};

export const mapSelector = (state: Rootstate) => state.map;

export default gridReducer;

